import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import {
  seniorityMatrixEntrySchema,
  type SeniorityMatrixEntryFormInput,
  type SeniorityMatrixEntryFormValues,
} from '../../schemas';

interface SeniorityTierFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingValues?: SeniorityMatrixEntryFormValues | null;
  onSave: (values: SeniorityMatrixEntryFormValues) => void;
}

const EMPTY_TIER: SeniorityMatrixEntryFormInput = { fromYear: 0, toYear: '', days: 0 };

/** Edits a single tier in memory only — the parent still batches the full array into one `PUT` on "Guardar matriz". */
export function SeniorityTierFormDialog({
  open,
  onOpenChange,
  editingValues,
  onSave,
}: SeniorityTierFormDialogProps) {
  const { t } = useTranslation('timeOff');

  const { control, handleSubmit, reset } = useForm<
    SeniorityMatrixEntryFormInput,
    unknown,
    SeniorityMatrixEntryFormValues
  >({
    resolver: zodResolver(seniorityMatrixEntrySchema(t)),
    defaultValues: EMPTY_TIER,
  });

  useEffect(() => {
    if (open) {
      reset(editingValues ? { ...editingValues, toYear: editingValues.toYear ?? '' } : EMPTY_TIER);
    }
  }, [open, editingValues, reset]);

  function onSubmit(values: SeniorityMatrixEntryFormValues) {
    onSave(values);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="time-off-settings-matrix-tier-dialog">
        <DialogHeader>
          <DialogTitle>
            {editingValues ? t('settings.matrix.editTier') : t('settings.matrix.addTier')}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
          data-testid="time-off-settings-matrix-tier-form"
        >
          <FormFieldInput
            control={control}
            name="fromYear"
            type="number"
            label={t('settings.matrix.fromYear')}
            data-testid="time-off-settings-matrix-tier-from"
          />
          <FormFieldInput
            control={control}
            name="toYear"
            type="number"
            label={t('settings.matrix.toYear')}
            placeholder={t('settings.matrix.openEnded')}
            data-testid="time-off-settings-matrix-tier-to"
          />
          <FormFieldInput
            control={control}
            name="days"
            type="number"
            label={t('settings.matrix.days')}
            data-testid="time-off-settings-matrix-tier-days"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('settings.matrix.tierCancel')}
            </Button>
            <Button type="submit" data-testid="time-off-settings-matrix-tier-submit">
              {t('settings.matrix.tierSave')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
